import { motion } from "framer-motion";
import { Workflow, Bot, Layers, Boxes, TrendingUp, BarChart3 } from "lucide-react";
import { useLang } from "@/i18n";

const ICONS = [Workflow, Bot, Layers, Boxes, TrendingUp, BarChart3];

const cardVariants = {
  hidden: { opacity: 0, y: 32 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Services = () => {
  const { t } = useLang();
  return (
    <section id="services" data-testid="services-section" className="relative py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 max-w-2xl"
        >
          <div className="font-mono-alt mb-4 text-xs uppercase tracking-[0.25em] text-blue-700">
            {t.services.eyebrow}
          </div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            {t.services.title} <span className="text-glow-gradient">{t.services.titleAccent}</span>
          </h2>
          <p className="mt-4 text-base font-normal leading-relaxed text-slate-700">{t.services.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <motion.article
                key={s.title}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                data-testid={`service-card-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-[0_16px_40px_rgba(29,78,216,0.10)] sm:p-8"
              >
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-500/60 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-700 transition-colors duration-300 group-hover:bg-blue-100">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="font-mono-alt rounded-full border border-slate-200 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    {s.tag}
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm font-normal leading-relaxed text-slate-700 sm:text-base">{s.desc}</p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
